import type { AsYouType, CountryCode } from "libphonenumber-js";
import { useCallback, useRef } from "react";
import type { Libphonenumber } from "./useLibPhoneNumber";

type FormatResult = {
	formatted: string;
	e164: string | undefined;
};

export function usePhoneFormatter(lib: Libphonenumber | null, country: CountryCode | undefined) {
	const asYouTypeRef = useRef<AsYouType | null>(null);
	const prevCountryRef = useRef<CountryCode | undefined>(undefined);
	const prevLibRef = useRef<Libphonenumber | null>(null);

	if (country !== prevCountryRef.current || lib !== prevLibRef.current) {
		prevCountryRef.current = country;
		prevLibRef.current = lib;
		asYouTypeRef.current = lib && country ? new lib.AsYouType(country) : null;
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: country+lib invalidate the callback since the AsYouType ref is re-created on either change
	const format = useCallback(
		(rawDigits: string): FormatResult => {
			const formatter = asYouTypeRef.current;
			if (!formatter) {
				return { formatted: rawDigits, e164: undefined };
			}

			formatter.reset();
			const formatted = formatter.input(rawDigits);
			const e164 = formatter.getNumberValue();

			return { formatted, e164: e164 ?? undefined };
		},
		[country, lib],
	);

	return { format };
}

export function countDigitsBefore(str: string, index: number): number {
	let count = 0;
	for (let i = 0; i < index && i < str.length; i++) {
		if (/\d/.test(str.charAt(i))) {
			count++;
		}
	}
	return count;
}

export function findIndexOfNthDigit(str: string, n: number): number {
	if (n <= 0) {
		return 0;
	}
	let count = 0;
	for (let i = 0; i < str.length; i++) {
		if (/\d/.test(str.charAt(i))) {
			count++;
			if (count === n) {
				return i + 1;
			}
		}
	}
	return str.length;
}
